import AddIcon from "@mui/icons-material/Add";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import ConfirmDialog from "../../components/ConfirmDialog";
import { PageHeader } from "../../components/PageHeader";
import { TableEmptyState } from "../../components/table/TableEmptyState";
import IconButton from "../../components/ui/IconButton";
import { useIntl } from "../../contexts/IntlContext";
import {
  YoutubeAdminAccount,
  createYoutubeAdminAccount,
  deleteYoutubeAdminAccount,
  listYoutubeAdminAccounts,
  unlinkYoutubeAdminAccount,
  updateYoutubeAdminAccount,
} from "../../services/youtubeAdminAccounts";
import YoutubeAccountFormDialog, {
  AccountFormValues,
} from "./components/YoutubeAccountFormDialog";
import YoutubeAdminAccountRow from "./components/YoutubeAdminAccountRow";

export default function YoutubeAccounts() {
  const intl = useIntl();
  const [accounts, setAccounts] = useState<YoutubeAdminAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<YoutubeAdminAccount | null>(null);
  const [unlinkingAccount, setUnlinkingAccount] = useState<YoutubeAdminAccount | null>(null);
  const [deletingAccount, setDeletingAccount] = useState<YoutubeAdminAccount | null>(null);

  const loadAccounts = useCallback(async (silent = false) => {
    setLoading(true);
    try {
      const data = await listYoutubeAdminAccounts();
      setAccounts(data);
    } catch {
      // Recarregamento logo após vincular uma conta: a conexão em si já foi confirmada pelo
      // callback OAuth, então uma falha aqui é só do refresh da lista (ver useYoutubeOAuthPopup),
      // não vale assustar o usuário com um erro sobre algo que já deu certo.
      if (!silent) {
        toast.error(intl.formatMessage({ id: "messages.connectionError" }));
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadAccounts();
  }, [loadAccounts]);

  const handleCreate = async (values: AccountFormValues) => {
    try {
      await createYoutubeAdminAccount(values);
      toast.success(intl.formatMessage({ id: "youtubeAdminAccounts.createSuccess" }));
      setCreateOpen(false);
      await loadAccounts();
    } catch (error: any) {
      const message = error?.response?.data?.message;
      toast.error(message || intl.formatMessage({ id: "messages.connectionError" }));
    }
  };

  const handleUpdate = async (values: AccountFormValues) => {
    if (!editingAccount) return;
    try {
      await updateYoutubeAdminAccount(editingAccount.id, values);
      toast.success(intl.formatMessage({ id: "youtubeAdminAccounts.updateSuccess" }));
      setEditingAccount(null);
      await loadAccounts();
    } catch (error: any) {
      const message = error?.response?.data?.message;
      toast.error(message || intl.formatMessage({ id: "messages.connectionError" }));
    }
  };

  const handleToggleActive = async (account: YoutubeAdminAccount) => {
    try {
      await updateYoutubeAdminAccount(account.id, { active: !account.active });
      await loadAccounts();
    } catch {
      toast.error(intl.formatMessage({ id: "messages.connectionError" }));
    }
  };

  const handleUnlink = async () => {
    if (!unlinkingAccount) return;
    try {
      await unlinkYoutubeAdminAccount(unlinkingAccount.id);
      toast.success(intl.formatMessage({ id: "youtubeAdminAccounts.unlinkSuccess" }));
    } catch {
      toast.error(intl.formatMessage({ id: "messages.connectionError" }));
    } finally {
      setUnlinkingAccount(null);
      await loadAccounts();
    }
  };

  const handleDelete = async () => {
    if (!deletingAccount) return;
    try {
      await deleteYoutubeAdminAccount(deletingAccount.id);
      toast.success(intl.formatMessage({ id: "youtubeAdminAccounts.deleteSuccess" }));
      await loadAccounts();
    } catch (error: any) {
      const message = error?.response?.data?.message;
      toast.error(message || intl.formatMessage({ id: "messages.connectionError" }));
    } finally {
      setDeletingAccount(null);
    }
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <PageHeader title={intl.formatMessage({ id: "youtubeAdminAccounts.title" })} loading={loading} sx={{ px: 2, py: 1 }}>
        <IconButton
          tooltip={intl.formatMessage({ id: "common.add" })}
          onClick={() => setCreateOpen(true)}
        >
          <AddIcon />
        </IconButton>
      </PageHeader>

      <TableContainer sx={{ maxHeight: "65vh" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>{intl.formatMessage({ id: "youtubeAdminAccounts.column.channel" })}</TableCell>
              <TableCell>{intl.formatMessage({ id: "common.status" })}</TableCell>
              <TableCell align="right">
                {intl.formatMessage({ id: "youtubeAdminAccounts.column.scheduleLimit" })}
              </TableCell>
              <TableCell>{intl.formatMessage({ id: "youtubeAdminAccounts.form.active" })}</TableCell>
              <TableCell>{intl.formatMessage({ id: "youtubeAdminAccounts.column.lastSyncedAt" })}</TableCell>
              <TableCell align="right">{intl.formatMessage({ id: "common.actions" })}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accounts.length === 0 ? (
              <TableEmptyState colSpan={6}>
                {intl.formatMessage({ id: "youtubeAdminAccounts.empty" })}
              </TableEmptyState>
            ) : (
              accounts.map(account => (
                <YoutubeAdminAccountRow
                  key={account.id}
                  account={account}
                  onChanged={() => loadAccounts(true)}
                  onToggleActive={() => handleToggleActive(account)}
                  onEdit={() => setEditingAccount(account)}
                  onUnlink={() => setUnlinkingAccount(account)}
                  onDelete={() => setDeletingAccount(account)}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <YoutubeAccountFormDialog
        open={createOpen}
        titleId="youtubeAdminAccounts.createTitle"
        initialValues={{ name: "", scheduleLimit: 10, active: true }}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />

      <YoutubeAccountFormDialog
        open={!!editingAccount}
        titleId="youtubeAdminAccounts.editTitle"
        initialValues={{
          name: editingAccount?.name ?? "",
          scheduleLimit: editingAccount?.scheduleLimit ?? 10,
          active: editingAccount?.active ?? true,
        }}
        disabledFields={editingAccount?.ownerManaged ? ["name", "active"] : undefined}
        onClose={() => setEditingAccount(null)}
        onSubmit={handleUpdate}
      />

      <ConfirmDialog
        open={!!unlinkingAccount}
        size="small"
        title={intl.formatMessage({ id: "youtubeAdminAccounts.unlinkConfirmTitle" })}
        message={intl.formatMessage(
          { id: "youtubeAdminAccounts.unlinkConfirmMessage" },
          { name: unlinkingAccount?.name ?? "" }
        )}
        onClose={() => setUnlinkingAccount(null)}
        onConfirm={handleUnlink}
      />

      <ConfirmDialog
        open={!!deletingAccount}
        size="small"
        title={intl.formatMessage({ id: "youtubeAdminAccounts.deleteConfirmTitle" })}
        message={intl.formatMessage(
          { id: "youtubeAdminAccounts.deleteConfirmMessage" },
          { name: deletingAccount?.name ?? "" }
        )}
        onClose={() => setDeletingAccount(null)}
        onConfirm={handleDelete}
      />
    </Paper>
  );
}
